/**
 * WHICH LIVE SESSION A GLOBAL HOTKEY SHOULD ACT ON.
 *
 * A voice room is a module singleton, so it can always be reached. A MEETING's
 * media client is owned by the meeting component tree and is deliberately not
 * global — nothing outside the room may construct or hold one. This registry is
 * the one exception: the room hands over just the controls a hotkey needs, for
 * exactly as long as it is mounted.
 *
 * A REGISTERED BINDING WINS OVER THE VOICE CHANNEL, and that is the opposite
 * of what this file used to say. The old rule was "voice wins when both are
 * live, which mirrors the product rule that you can only really be talking in
 * one place at a time". That reasoning holds for two rooms on screen together.
 * It does not hold for the case it actually decided: a binding is registered
 * only by a LIVE MEETING OR CALL ROOM, and those routes live OUTSIDE `AppShell`
 * — so whenever there is a binding, the voice dock is unmounted and the meeting
 * is the room on screen. Voice-first meant the global mute key acted on a voice
 * channel the user could not see, in a document whose only microphone indicator
 * belonged to the meeting and did not move. "Describes one room and acts on
 * another" is the exact defect main/mediaRouter.ts exists to prevent, and it was
 * happening one layer below it, inside a single document.
 *
 * `useDesktopMediaState` uses the same order, so what the tray SAYS about this
 * document and what the key DOES to it cannot disagree.
 *
 * ONE CONTROL, NOT TWO. `setTransmitting` used to live on this binding, for the
 * global push-to-talk gate. That gate is gone (apps/desktop/src/main/hotkeys.ts)
 * and with it the only caller: nothing outside a room may reach for the
 * published-track mute any more, which is the correct blast radius for a
 * control whose failure mode is a microphone somebody else can hear.
 */
import { createStore } from '@/lib/tinyStore';

export interface DesktopMediaBinding {
  readonly micOn: boolean;
  /** The big mute button: stops capture, so the OS mic indicator goes dark. */
  setMicEnabled(on: boolean): void;
}

export const mediaBindingStore = createStore<DesktopMediaBinding | null>(null);

/** Called by the live meeting room. Returns the unregister function. */
export function registerDesktopMedia(binding: DesktopMediaBinding): () => void {
  mediaBindingStore.set(binding);
  return () => {
    if (mediaBindingStore.get() === binding) mediaBindingStore.set(null);
  };
}

export function activeMediaBinding(): DesktopMediaBinding | null {
  return mediaBindingStore.get();
}
